// A small, deliberately boring markdown renderer for agent replies.
//
// It builds React elements, never an HTML string, so nothing the agent writes can become markup.
// It covers what agents actually send: paragraphs, bullet and numbered lists, headings, fenced
// code, inline code, bold, italics and plain links. Anything else stays as text.
import React from "react";

const INLINE = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\((?:https?:\/\/)[^\s)]+\))/g;

function inline(text, keyPrefix) {
  const parts = String(text).split(INLINE).filter((piece) => piece !== "" && piece !== undefined);
  return parts.map((piece, index) => {
    const key = `${keyPrefix}-${index}`;
    if (piece.startsWith("**") && piece.endsWith("**")) return <strong key={key}>{piece.slice(2, -2)}</strong>;
    if (piece.startsWith("`") && piece.endsWith("`")) {
      return <code key={key} className="rounded bg-sm-surface px-1 py-0.5 text-[0.85em]">{piece.slice(1, -1)}</code>;
    }
    if (piece.startsWith("*") && piece.endsWith("*")) return <em key={key}>{piece.slice(1, -1)}</em>;
    const link = piece.match(/^\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)$/);
    if (link) {
      return (
        <a key={key} href={link[2]} target="_blank" rel="noopener noreferrer" className="text-sm-accent underline">
          {link[1]}
        </a>
      );
    }
    return <React.Fragment key={key}>{piece}</React.Fragment>;
  });
}

export function Markdown({ text }) {
  const source = String(text ?? "");
  if (!source.trim()) return null;

  const blocks = [];
  const lines = source.split("\n");
  let list = null;
  let code = null;

  const flushList = () => {
    if (!list) return;
    const Tag = list.ordered ? "ol" : "ul";
    blocks.push(
      <Tag
        key={`b${blocks.length}`}
        className={`my-1 space-y-1 ${list.ordered ? "list-decimal" : "list-disc"} pl-5`}
      >
        {list.items.map((item, index) => <li key={index}>{inline(item, `l${blocks.length}-${index}`)}</li>)}
      </Tag>,
    );
    list = null;
  };

  lines.forEach((raw, lineIndex) => {
    const line = raw.replace(/\s+$/, "");
    if (line.trim().startsWith("```")) {
      if (code === null) {
        flushList();
        code = [];
      } else {
        blocks.push(
          <pre key={`b${blocks.length}`} className="my-2 overflow-x-auto rounded bg-sm-surface p-2 text-xs">
            <code>{code.join("\n")}</code>
          </pre>,
        );
        code = null;
      }
      return;
    }
    if (code !== null) {
      code.push(raw);
      return;
    }

    const bullet = line.match(/^\s*[-*]\s+(.*)$/);
    const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const ordered = Boolean(numbered);
      if (!list || list.ordered !== ordered) {
        flushList();
        list = { ordered, items: [] };
      }
      list.items.push((bullet || numbered)[1]);
      return;
    }

    flushList();
    if (!line.trim()) return;

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      blocks.push(
        <p key={`b${blocks.length}`} className="mt-2 font-semibold text-sm-ink">
          {inline(heading[2], `h${lineIndex}`)}
        </p>,
      );
      return;
    }
    blocks.push(
      <p key={`b${blocks.length}`} className="my-1 whitespace-pre-wrap">{inline(line, `p${lineIndex}`)}</p>,
    );
  });

  flushList();
  if (code !== null && code.length) {
    blocks.push(
      <pre key={`b${blocks.length}`} className="my-2 overflow-x-auto rounded bg-sm-surface p-2 text-xs">
        <code>{code.join("\n")}</code>
      </pre>,
    );
  }
  return <>{blocks}</>;
}

export default Markdown;
